import './header.css';
import { useEffect, useRef, useState } from 'react';
import Menu from '../flyout-menu/flyout-menu.tsx';
import { useBackgroundColor } from '../../services/hooks/useBackgroundColor';
import MenuIcon from '../../../../assets/features/workplace/icons/menu.svg?react';
import HomeIcon from '../../../../assets/features/workplace/icons/home.svg?react';
import ShopIcon from '../../../../assets/features/shop/icons/shop.svg?react';
import ShoppingCartIcon from '../../../../assets/features/shop/icons/shopping-cart.svg?react';
import NotificationIcon from '../../../../assets/features/workplace/icons/notifications.svg?react';
import UserIcon from '../../../../assets/features/identity-access-management/icons/user.svg?react';
import LogoSvg from '../../../../assets/features/workplace/images/logo-suus-beverages.svg?react';
import { useAuth } from '@/app/identity-access-management/authentication/context/AuthContext.tsx';
import { useShoppingCart } from '@/app/shop/features/shopping-cart/shopping-cart.context.tsx';
import {Avatar, Icon} from "suus-component-library";

export default function Header() {
    const logoRef = useRef<HTMLAnchorElement>(null);

    /*
     * "light" bedeutet:
     * Der Hintergrund ist hell und das Logo soll dunkel sein.
     *
     * "dark" bedeutet:
     * Der Hintergrund ist dunkel und das Logo soll weiss sein.
     */
    const backgroundTone = useBackgroundColor(logoRef);

    const [isFlyoutMenuOpen, setFlyoutMenuOpen] = useState(false);
    const [isTopMenuShown, setTopMenuShown] = useState(true);

    /*
     * Ein Ref reicht hier aus. Dadurch muss der Scroll-Listener
     * nicht nach jedem Scrollen neu registriert werden.
     */
    const lastScrollYRef = useRef(0);

    const { state } = useAuth();
    const { totalItems } = useShoppingCart();
    const headerAvatarSrc = state.status === "authenticated"
        ? state.userAccount.avatarUrl
        : undefined;

    const toggleFlyoutMenu = () => {
        setFlyoutMenuOpen((currentValue) => !currentValue);
    };

    const closeFlyoutMenu = () => {
        setFlyoutMenuOpen(false);
    };

    useEffect(() => {
        const controlTopMenu = () => {
            const currentScrollY = window.scrollY;
            const isScrollingUp =
                currentScrollY < lastScrollYRef.current;

            /*
             * Am Seitenanfang bleibt das Menü immer sichtbar.
             */
            const isNearPageTop = currentScrollY < 20;

            setTopMenuShown(isScrollingUp || isNearPageTop);

            lastScrollYRef.current = currentScrollY;
        };

        window.addEventListener('scroll', controlTopMenu, {
            passive: true,
        });

        return () => {
            window.removeEventListener(
                'scroll',
                controlTopMenu,
            );
        };
    }, []);

    return (
        <>
            <header
                className={[
                    'site-header',
                    !isTopMenuShown
                        ? 'site-header--hidden'
                        : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <div className="menu-logo">
                    <a
                        ref={logoRef}
                        href="/home"
                        className="logo-wrapper"
                        data-background={backgroundTone}
                        aria-label="SUUS Logo"
                    >
                        <LogoSvg
                            className="logo-svg"
                            aria-hidden="true"
                            focusable="false"
                        />
                    </a>
                </div>

                <div className="menu-bar">
                    <div className="menu-hamburger">
                        <button
                            id="idIconHamburger"
                            className="menu-icon-button"
                            type="button"
                            onClick={toggleFlyoutMenu}
                            aria-label="Menü öffnen"
                            aria-expanded={isFlyoutMenuOpen}
                        >
                            <MenuIcon
                                className="icon"
                                aria-hidden="true"
                                focusable="false"
                            />
                        </button>
                    </div>

                    <nav
                        className="menu-items"
                        aria-label="Hauptnavigation"
                    >
                        <menu>
                            <li>
                                <a href="/home">Home</a>
                            </li>

                            <li>
                                <a href="/shop">Shop</a>
                            </li>

                            <li>
                                <a href="/company">Company</a>
                            </li>

                            <li>
                                <a href="/stories">Stories</a>
                            </li>
                        </menu>
                    </nav>

                    <div className="menu-icons">
                        <a
                            id="idIconHome"
                            href="/home"
                            aria-label="Home"
                        >
                            <HomeIcon
                                className="icon"
                                aria-hidden="true"
                                focusable="false"
                            />
                        </a>

                        <a
                            id="idIconShop"
                            href="/shop"
                            aria-label="Shop"
                        >
                            <ShopIcon
                                className="icon"
                                aria-hidden="true"
                                focusable="false"
                            />
                        </a>

                        <a
                            id="idIconShoppingCart"
                            href="/shop/shopping-cart"
                            aria-label={`Warenkorb mit ${totalItems} Artikeln`}
                        >
                            <Icon
                                icon={<ShoppingCartIcon />}
                                badgeCount={totalItems}
                                badgeColor="green"
                            />
                        </a>

                        <a
                            id="idIconNotificationCenter"
                            href="/workplace/notification-center"
                            aria-label="Benachrichtigungen"
                        >
                            <NotificationIcon
                                className="icon"
                                aria-hidden="true"
                                focusable="false"
                            />
                        </a>

                        <a
                            id="idIconUser"
                            href={
                                state.status === 'authenticated'
                                    ? '/auth/user-profile'
                                    : '/auth/user-login'
                            }
                            aria-label={
                                state.status === 'authenticated'
                                    ? 'Benutzerprofil'
                                    : 'Benutzer anmelden'
                            }
                        >
                            {(!headerAvatarSrc || state.status !== 'authenticated') && (
                                <UserIcon
                                    className="icon"
                                    aria-hidden="true"
                                    focusable="false"
                                />
                            )}

                            {state.status === 'authenticated' && headerAvatarSrc && (
                                    <Avatar
                                        src={headerAvatarSrc}
                                        alt="Benutzerprofil"
                                    />
                                )}
                        </a>
                    </div>
                </div>
            </header>

            {isFlyoutMenuOpen && (
                <Menu closeMenu={closeFlyoutMenu} />
            )}
        </>
    );
}
